import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig, AxiosError } from 'axios';
import { axiosInstance } from './axiosConfig';

// Global auth state - will be updated by AuthProvider
let authState = {
    isLoaded: false,
    isSignedIn: false,
    getToken: null as (() => Promise<string | null>) | null,
    orgId: null as string | null,
};

// Function to update auth state from AuthProvider
export const updateAuthState = (newAuthState: {
    isLoaded: boolean;
    isSignedIn: boolean;
    getToken: (() => Promise<string | null>) | null;
    orgId?: string | null;
}) => {
    authState = { ...authState, ...newAuthState };
};

// Helper function to wait for auth to be loaded
const waitForAuth = (): Promise<boolean> => {
    return new Promise((resolve) => {
        if (authState.isLoaded) {
            resolve(authState.isSignedIn);
            return;
        }

        // Poll for auth to be loaded (with timeout)
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max wait
        const interval = setInterval(() => {
            attempts++;

            if (authState.isLoaded || attempts >= maxAttempts) {
                clearInterval(interval);
                resolve(authState.isSignedIn);
            }
        }, 100);
    });
};

const authAwareAxiosBaseQuery =
    (): BaseQueryFn<
        {
            url: string;
            method: AxiosRequestConfig['method'];
            data?: AxiosRequestConfig['data'];
            body?: AxiosRequestConfig['data'];
            params?: AxiosRequestConfig['params'];
            headers?: AxiosRequestConfig['headers'];
            skipAuth?: boolean; // Allow skipping auth for public endpoints
        },
        unknown,
        unknown
    > =>
        async ({ url, method, data, body, params, headers, skipAuth = false }) => {
            try {
                // Skip auth check for public endpoints
                if (!skipAuth) {
                    // Wait for authentication to be ready
                    const isSignedIn = await waitForAuth();

                    if (!isSignedIn) {
                        return {
                            error: {
                                status: 401,
                                data: { message: 'Authentication required' },
                            },
                        };
                    }

                    // Ensure we have a fresh token before making the request
                    if (authState.getToken) {
                        try {
                            const token = await authState.getToken();
                            if (!token) {
                                return {
                                    error: {
                                        status: 401,
                                        data: { message: 'Unable to obtain authentication token' },
                                    },
                                };
                            }
                        } catch (tokenError) {
                            return {
                                error: {
                                    status: 401,
                                    data: { message: 'Token refresh failed' },
                                },
                            };
                        }
                    }
                }

                const requestBody = body || data;

                // --- INJECT ORG ID CONTEXT ---
                const customHeaders: Record<string, string> = {
                    ...(headers as Record<string, string> || {}),
                };

                let customParams = { ...(params || {}) };

                if (authState.orgId) {
                    customHeaders['x-org-id'] = authState.orgId;
                    customHeaders['Organization-ID'] = authState.orgId;

                    customParams = {
                        ...customParams,
                        org_id: authState.orgId
                    };
                }

                const result = await axiosInstance({
                    url,
                    method,
                    data: requestBody,
                    params: customParams,
                    headers: customHeaders,
                    withCredentials: true, // send cookies with every request
                });

                return { data: result.data };
            } catch (axiosError) {
                const err = axiosError as AxiosError;

                // Handle 401 errors - might need token refresh
                if (err.response?.status === 401 && authState.getToken) {
                    try {
                        // Try to get a fresh token
                        await authState.getToken();
                    } catch (refreshError) {
                        // Token refresh failed - will be handled by error return
                    }
                }

                return {
                    error: {
                        status: err.response?.status,
                        data: err.response?.data || err.message,
                    },
                };
            }
        };

export const baseApi = createApi({
    reducerPath: 'threatIntelApi',
    baseQuery: authAwareAxiosBaseQuery(),
    tagTypes: [
        'ThreatIntel',
        'Incident',
        'Organization',
        'User',
        'Search',
        'OrganizationUser',
        'DashboardStats',
        'ThreatSummary',
        'ThreatData',
        'Watchlist',
        'ThreatFeedStats',
        'ThreatFeedPost',
        'ThreatFeedStats',
        'ThreatFeedTaxonomy',
        'DomainVerification',
        'Reports',
    ],
    endpoints: () => ({}),
});

export default baseApi;